import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Box } from '@material-ui/core';

import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import { FileDto } from '../../documents/Document.interface';
import DocumentTemplateDialog from '../../documents/components/DocumentTemplateDialog';
import { useUpdateProductTemplate } from '../Product.hooks';
import { Product, ProductTemplate } from '../Product.interface';

export interface AssignDocumentTemplateDialogProps {
  open: boolean;
  product: Product;
  template: ProductTemplate;
  onCancel: () => void;
  onSuccess: (template: ProductTemplate) => void;
}

const AssignDocumentTemplateDialog: React.FC<AssignDocumentTemplateDialogProps> = (
  props: AssignDocumentTemplateDialogProps
) => {
  const {
    open,
    product,
    template: initialTemplate,
    onCancel,
    onSuccess
  } = props;
  const onSuccessRef = useRef(onSuccess);
  const [selectedFile, setSelectedFile] = useState<FileDto | undefined>();
  const [template, setTemplate] = useState<ProductTemplate>(initialTemplate);
  const {
    updatedProductTemplate,
    isUpdating,
    updateError,
    updateProductTemplate
  } = useUpdateProductTemplate(product.id, initialTemplate.id);

  const onAssign = (file: FileDto | undefined) => {
    updateProductTemplate({ name: template.name, fileId: file?.id || null });
  };

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    setTemplate(initialTemplate);
  }, [initialTemplate]);

  useEffect(() => {
    setSelectedFile(template.file);
  }, [template]);

  useEffect(() => {
    if (updatedProductTemplate) {
      onSuccessRef.current(updatedProductTemplate);
    }
  }, [updatedProductTemplate]);

  return (
    <InlineFormik
      isSubmitting={isUpdating}
      initialValues={{ name: template.name }}
      validationSchema={Yup.object({
        name: Yup.string().required('Template name is required.')
      })}
      onSubmit={(form: Partial<ProductTemplate>) => {
        setTemplate(template => ({ ...template, ...form }));
      }}
    >
      {({ isValid }) => (
        <DocumentTemplateDialog
          open={open}
          file={selectedFile}
          error={updateError}
          isLoading={isUpdating}
          confirmLabel='Assign'
          title='Assign Document Template'
          forceConfirmButton={isValid && template.name !== initialTemplate.name}
          setOpen={(open: boolean) => !open && onCancel()}
          onSelectFile={(file?: FileDto) => {
            setSelectedFile(file);
            onAssign(file);
          }}
          content={
            <Box mb={2}>
              <InlineForm>
                <InlineTextField
                  name='name'
                  label='Set Template Name'
                  placeholder='Provide a name for the template'
                />
              </InlineForm>
            </Box>
          }
        />
      )}
    </InlineFormik>
  );
};

export default AssignDocumentTemplateDialog;
